import axios from 'axios';
import configuredStore from '../../../core/configureReduxStore';
// import { STATE__AUTHORIZED } from '../../constants';

import {
  loadAuthorizationJwtTokenFromLocalStorage,
  loadAuthorizationRefreshTokenFromLocalStorage,
  saveAuthorizationJwtTokenInLocalStorage,
  saveAuthorizationRefreshTokenInLocalStorage,
  deleteAuthorizationJwtTokenFromLocalStorage,
  deleteAuthorizationRefreshTokenFromLocalStorage,
} from '../../helpers/localStorageHelper';

import * as constants from './constants';
import { setFormMessage } from '../../unauthorizedState/actions'; // eslint-disable-line
import { setExpirationDate } from '../../authorizedState/actions';

const onAuthorizationHeaderNeeded = () => {
  return `Bearer ${loadAuthorizationJwtTokenFromLocalStorage()}`;
};

const deleteRequest = (url, params, data, contentType = constants.HEADER_VALUE__CONTENT_TYPE, cancelToken = undefined) => {
  return axios({
    url: `${window.env.API_URL}/${url}`,
    params,
    method: constants.HTTP_METHOD__DELETE,
    data,
    headers: {
      [constants.HEADER_NAME__AUTHORIZATION]: onAuthorizationHeaderNeeded(),
      [constants.HEADER_NAME__CACHE_CONTROL]: constants.HEADER_VALUE__CACHE_CONTROL,
      [constants.HEADER_NAME__CONTENT_TYPE]: contentType,
      [constants.HEADER_NAME__EXPIRES]: constants.HEADER_VALUE__EXPIRES,
      [constants.HEADER_NAME__PRAGMA]: constants.HEADER_VALUE__PRAGMA,
    },
    timeout: constants.TIMEOUT,
    cancelToken,
  });
};

const getRequest = (url, params, responseType = constants.RESPONSE_TYPE__JSON, cancelToken = undefined) => {
  return axios({
    url: `${window.env.API_URL}/${url}`,
    params,
    method: constants.HTTP_METHOD__GET,
    headers: {
      [constants.HEADER_NAME__AUTHORIZATION]: onAuthorizationHeaderNeeded(),
      [constants.HEADER_NAME__CACHE_CONTROL]: constants.HEADER_VALUE__CACHE_CONTROL,
      [constants.HEADER_NAME__EXPIRES]: constants.HEADER_VALUE__EXPIRES,
      [constants.HEADER_NAME__PRAGMA]: constants.HEADER_VALUE__PRAGMA,
    },
    responseType,
    timeout: constants.TIMEOUT,
    cancelToken,
  });
};

const patchRequest = (url, params, data, contentType = constants.HEADER_VALUE__CONTENT_TYPE, cancelToken = undefined) => {
  return axios({
    url: `${window.env.API_URL}/${url}`,
    params,
    method: constants.HTTP_METHOD__PATCH,
    data,
    headers: {
      [constants.HEADER_NAME__AUTHORIZATION]: onAuthorizationHeaderNeeded(),
      [constants.HEADER_NAME__CACHE_CONTROL]: constants.HEADER_VALUE__CACHE_CONTROL,
      [constants.HEADER_NAME__CONTENT_TYPE]: contentType,
      [constants.HEADER_NAME__EXPIRES]: constants.HEADER_VALUE__EXPIRES,
      [constants.HEADER_NAME__PRAGMA]: constants.HEADER_VALUE__PRAGMA,
    },
    timeout: constants.TIMEOUT,
    cancelToken,
  });
};

const postRequest = (url, params, data, contentType = constants.HEADER_VALUE__CONTENT_TYPE, onUploadProgress = undefined, cancelToken = undefined) => {
  return axios({
    url: `${window.env.API_URL}/${url}`,
    params,
    method: constants.HTTP_METHOD__POST,
    data,
    headers: {
      [constants.HEADER_NAME__AUTHORIZATION]: onAuthorizationHeaderNeeded(),
      [constants.HEADER_NAME__CACHE_CONTROL]: constants.HEADER_VALUE__CACHE_CONTROL,
      [constants.HEADER_NAME__CONTENT_TYPE]: contentType,
      [constants.HEADER_NAME__EXPIRES]: constants.HEADER_VALUE__EXPIRES,
      [constants.HEADER_NAME__PRAGMA]: constants.HEADER_VALUE__PRAGMA,
    },
    onUploadProgress,
    timeout: constants.TIMEOUT,
    cancelToken,
  });
};

const downloadRequest = (url, params, data, getMethod) => {
  let method = '';
  if (getMethod) {
    method = constants.HTTP_METHOD__GET;
  } else {
    method = constants.HTTP_METHOD__POST;
  }

  return axios({
    url: `${window.env.API_URL}/${url}`,
    params,
    method,
    data,
    headers: {
      [constants.HEADER_NAME__AUTHORIZATION]: onAuthorizationHeaderNeeded(),
      [constants.HEADER_NAME__CACHE_CONTROL]: constants.HEADER_VALUE__CACHE_CONTROL,
      [constants.HEADER_NAME__CONTENT_TYPE]: constants.HEADER_VALUE__CONTENT_TYPE,
      [constants.HEADER_NAME__EXPIRES]: constants.HEADER_VALUE__EXPIRES,
      [constants.HEADER_NAME__PRAGMA]: constants.HEADER_VALUE__PRAGMA,
    },
    responseType: constants.RESPONSE_TYPE__BLOB,
    timeout: constants.TIMEOUT,
  });
};

// Obsługa tokenów odświeżających sesje.

let isTokenRefreshing = false;
let authSuccessHandlers = [];

const addAuthSucessHandler = (handler) => {
  authSuccessHandlers.push(handler);
};

const onTokenRefreshedSuccess = (token) => {
  authSuccessHandlers = authSuccessHandlers.filter((handler) => handler(token));
};

const prolongateSessionImpl = () => {
  const authorizationJwtTokenFromLocalStorage = loadAuthorizationJwtTokenFromLocalStorage();
  const authorizationRefreshTokenFromLocalStorage = loadAuthorizationRefreshTokenFromLocalStorage();

  if (authorizationJwtTokenFromLocalStorage && authorizationRefreshTokenFromLocalStorage) {
    if (!isTokenRefreshing) {
      isTokenRefreshing = true;

      axios({
        url: `${window.env.API_URL}/Authentication`,
        method: constants.HTTP_METHOD__PATCH,
        data: {
          token: authorizationJwtTokenFromLocalStorage,
          refreshToken: authorizationRefreshTokenFromLocalStorage,
        },
        headers: {
          [constants.HEADER_NAME__CACHE_CONTROL]: constants.HEADER_VALUE__CACHE_CONTROL,
          [constants.HEADER_NAME__CONTENT_TYPE]: constants.HEADER_VALUE__CONTENT_TYPE,
          [constants.HEADER_NAME__EXPIRES]: constants.HEADER_VALUE__EXPIRES,
          [constants.HEADER_NAME__PRAGMA]: constants.HEADER_VALUE__PRAGMA,
        },
        timeout: constants.TIMEOUT,
      }).then((response) => {
        isTokenRefreshing = false;

        const {
          token,
          refreshToken,
          refreshTokenExpireDate,
        } = response.data;

        saveAuthorizationJwtTokenInLocalStorage(token);
        saveAuthorizationRefreshTokenInLocalStorage(refreshToken);
        configuredStore.store.dispatch(setExpirationDate(refreshTokenExpireDate));

        onTokenRefreshedSuccess(token);
      }).catch(() => {
        isTokenRefreshing = false;

        deleteAuthorizationJwtTokenFromLocalStorage();
        deleteAuthorizationRefreshTokenFromLocalStorage();

        configuredStore.persistor.purge();
        configuredStore.store.dispatch(setFormMessage('Twoja sesja wygasła prosimy zalogować się ponownie.'));
      });
    }
  }
};

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { config, response: { status = -1, data = [] } = {} } = error;
    const requestToRetry = config;

    if (status === 401 && !data) {
      prolongateSessionImpl();

      return new Promise((resolve) => {
        addAuthSucessHandler((token) => {
          requestToRetry.headers.Authorization = `Bearer ${token}`;
          resolve(axios(requestToRetry));
        });
      });
    }

    return Promise.reject(error);
  },
);

export default {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  downloadRequest,
};
